/**
 * An image that cannot break the page (spec 42 R6).
 *
 * A missing key, a deleted object, or a failed fetch all resolve to the same
 * token-textured band — never a broken `<img>`, never a collapsed layout. The
 * fallback is drawn from tokens, so it follows light/dark for free.
 *
 * Dimensions are always reserved via `aspectRatio`, so swapping a real image
 * for the fallback (or the other way) shifts nothing on the page.
 */
import { useState } from "react";

const TEXTURE =
  "repeating-linear-gradient(45deg, var(--line-2) 0 1px, transparent 1px 8px)";

export function SectionImage({
  src,
  alt,
  aspectRatio,
  eager = false,
  radius = "var(--r)",
}: {
  src?: string | null;
  alt: string;
  /** CSS aspect-ratio, e.g. "16 / 5". Always set — it reserves the space. */
  aspectRatio: string;
  /** The first hero slide is above the fold; everything else lazy-loads. */
  eager?: boolean;
  radius?: string;
}) {
  const [failed, setFailed] = useState(false);
  const showFallback = !src || failed;

  return (
    <div
      style={{
        aspectRatio,
        width: "100%",
        overflow: "hidden",
        borderRadius: radius,
        backgroundColor: "var(--bg)",
        // The texture only paints on the fallback; a loaded image covers it.
        backgroundImage: showFallback ? TEXTURE : undefined,
        opacity: showFallback ? 0.85 : 1,
      }}
      // Decorative when there's nothing to describe.
      role={showFallback ? "presentation" : undefined}
    >
      {showFallback ? null : (
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          // Above-the-fold images should not queue behind lazy ones.
          {...(eager ? { fetchPriority: "high" as const } : {})}
          onError={() => setFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
    </div>
  );
}
